import styles from './Profile.module.scss';
import {environment} from '@/commons/configs/Config';
import config from '@/commons/configs/Config.json';
import ApiInterface from '@/commons/api/ApiInterface.js';
import React, {useEffect, useState} from 'react';
import {sha256Encrypt} from '@/commons/crypt/Encryption';
import Cookies from 'js-cookie';
import Header from '@/commons/pages/borders/header/Header';
import Footer from '@/commons/pages/borders/footer/Footer';
import {useInfoBox} from '@/commons/infoBox/InfoBox';
import FloatingInput from '@/commons/components/fields/floatingInput/FloatingInput';
import CustomButton from '@/commons/components/buttons/customButton/CustomButton';
import {useUser} from "@/contexts/UserContext";
import {useLocation} from "react-router-dom";
import CloudImage from "@/commons/components/image/CloudImage";
import {LazyLoadImage} from "react-lazy-load-image-component";

const Profile = () => {
    const {addInfo} = useInfoBox();
    const {accountData, minecraftData, twitchData, reportsData} = useUser();
    const [page, setPage] = useState<'profile' | 'connections' | 'settings'>('profile');
    const [settingType, setSettingType] = useState<'none' | 'email' | 'password'>('none');
    const [sendingEmailButtonDisabled, setSendingEmailButtonDisabled] = useState<boolean>(false);
    const location = useLocation();

    useEffect(() => {
        const url = new URL(window.location.href);
        const pageValue = url.searchParams.getAll('page');

        pageValue.forEach((pageValue) => {
            if (!pageValue) return;
            if (pageValue !== 'profile' && pageValue !== 'connections' && pageValue !== 'settings') return;

            setPage(pageValue)
            url.searchParams.delete('page');
            window.history.replaceState({}, '', url);
        });
    }, []);

    useEffect(() => {
        const hash = location.hash?.replace("#", "");

        if (!hash) return;

        switch (hash) {
            case "twitchconnection":
                window.close();
                break;
            case "connections":
                setPage("connections");
                break;
            case "settings":
                setPage("settings");
                break;
        }

        // Rimuove l’hash dall’URL
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }, [location]);

    useEffect(() => {
        if (page === 'settings') setSettingType('none');
    }, [page]);

    const sendVerifyEmail = () => {
        setSendingEmailButtonDisabled(true)
        ApiInterface.sendVerifyEmail().then((response) => {
            if (response.status === 200) {
                window.addInfo("success", "Email mandata", "Abbiamo mandato una nuova email di verifica\n"+
                    "Ricorda di controllare anche la cartella SPAM", 10)
                setTimeout(() => {
                    setSendingEmailButtonDisabled(false)
                }, 1500)
            } else {
                window.addInfo("alert", "Errore", "Errore interno durante l'invio della email", 10)
                setTimeout(() => {
                    setSendingEmailButtonDisabled(false)
                }, 1500)
            }
        })
    }

    const fetchMinecraftToken = async () => {
        const userToken = Cookies.get(environment.accessToken) || '';
        return await fetch(`${environment.apiUrlOne}/token/create`, {
            method: 'POST',
            headers: {
                Authorization: userToken,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({scope: 'minecraft_link'}),
        });
    };

    const loadTwitchConnection = () => {
        let twLink = `https://id.twitch.tv/oauth2/authorize?client_id=${config.twitch_app_id}&redirect_uri=${config.twitch_redirect_uri}&response_type=code&scope=user:read:email&state`;

        popUpConnection(twLink);
    };

    const loadMinecraftToken = async () => {
        const token = await fetchMinecraftToken();
        if (token.status === 200) {
            const res = await token.json();
            await navigator.clipboard.writeText(`/reports link ${res.token}`);
            addInfo('info', 'Token copiato', `/reports link ${res.token}`);
        }
    };

    const changeAccountData = async () => {
        const emailVal = (document.getElementById('email') as HTMLInputElement)?.value;
        const newPassword = (document.getElementById('newPassword') as HTMLInputElement)?.value;
        const newPasswordConfirm = (document.getElementById('newPasswordConfirm') as HTMLInputElement)?.value;
        const password = (document.getElementById('password') as HTMLInputElement)?.value;

        if (!password) {
            addInfo('alert', 'Errore', 'Inserisci la password attuale', 7);
            return;
        }

        const body: any = {currentPassword: await sha256Encrypt(password)};

        if (settingType === 'email') {
            if (!emailVal || emailVal.length < 5 || !emailVal.includes('@') || !emailVal.includes('.') || emailVal.includes(' ')) {
                addInfo('alert', 'Errore', 'Email non valida', 7);
                return;
            }
            body.email = emailVal;
        }

        if (settingType === 'password') {
            if (!newPassword || newPassword.length < 8 || newPassword !== newPasswordConfirm) {
                addInfo('alert', 'Errore', 'Password non valida o non coincidono', 7);
                return;
            }
            body.newPassword = await sha256Encrypt(newPassword);
        }

        const res = await ApiInterface.changeAccountData(Cookies.get(environment.accessToken), body);
        if (res.status === 200) {
            addInfo('info', 'Modificato', 'Dati aggiornati con successo', 7);
            setSettingType('none');
        } else if (res.status === 409) {
            addInfo('alert', 'Errore', 'Email già registrata', 7);
        } else if (res.status === 418) {
            const json = await res.json();
            if (json?.object) {
                addInfo(json.object.alert, json.object.title, json.object.message, 7);
            }
        } else {
            addInfo('alert', 'Errore', 'Errore interno', 7);
        }
    };

    const getMinecraftBox = () => {
        if (!accountData?.emailVerified) {
            return <span className={styles.notConnected}>Email non verificata</span>;
        }

        if (minecraftData?.username) {
            return (
                <>
                    <span className={styles.connected}>Minecraft collegato</span>
                    <span className={styles.sectionTinyInfo}>{minecraftData.username}</span>
                    {minecraftData?.uuid ? (
                        <>
                            <LazyLoadImage className={styles.skinHead}
                                           src={"https://mc-heads.net/head/" + minecraftData.uuid + "/right/.png"}/>
                        </>
                    ) : (
                        <>
                            <LazyLoadImage className={styles.skinHead} style={{filter: "grayscale(1)"}}
                                           src={"https://mc-heads.net/head/steve/right/.png"}/>
                        </>
                    )}
                    <CustomButton onClick={loadMinecraftToken}>Cambia account</CustomButton>
                </>
            );
        }

        return (
            <>
                <span className={styles.notConnected}>Nessun account collegato</span>
                <p>Usa <span className={styles.goldColor}>/reports link [token]</span> nel server Minecraft</p>
                <CustomButton onClick={loadMinecraftToken}>Collega Minecraft</CustomButton>
            </>
        );
    };

    function popUpConnection(twLink: string) {
        let popup = window.open(twLink, "TwitchLink", 'width=1000,height=800,scrollbars=yes');
        let i = 0;

        const popupChecker = setInterval(() => {
            if (popup === null) {
                i++;
                if (i > 10) {
                    clearInterval(popupChecker);
                    window.location.reload(); // ricarica la pagina
                }
                return;
            }
            if (popup.closed) {
                clearInterval(popupChecker);
                window.location.hash = page;
                window.location.reload();
            }
        }, 500);
    }

    const getTwitchBox = () => {
        if (!accountData?.emailVerified) {
            return <span className={styles.notConnected}>Email non verificata</span>;
        }

        if (minecraftData !== undefined) {
            if (twitchData !== undefined) {
                return TwitchData(true);
            } else {
                //Twitch Link: Minecraft collegato ma Twitch no
                let twLink = `https://twitchlink.erenblaze.it/connect?code=${minecraftData?.authCode}&redirect=${encodeURIComponent(document.location.href + "#twitchconnection")}`;
                return (
                    <>
                        <span className={styles.notConnected}>Nessun account collegato</span>
                        <CustomButton onClick={() => {
                            popUpConnection(twLink);
                        }}>
                            Collega TwitchLink a Minecraft
                        </CustomButton>
                    </>
                )
            }
        } else {
            if (twitchData !== undefined) {
                return TwitchData(false);
            } else {
                return (
                    <>
                        <span className={styles.notConnected}>Nessun account collegato</span>
                        <span className={styles.sectionTinyInfo}>Puoi collegare il tuo account Twitch all'account EBLCraft (non influirà sul server EBLCraft).</span>
                        <CustomButton onClick={loadTwitchConnection}>Collega account</CustomButton>
                    </>
                )
            }
        }
    };

    const TwitchData = (minecraftConnected: boolean) => {
        return (
            <>
                <span className={styles.connected}>{minecraftConnected ? "Account Twitch preso in automatico da" +
                    " Minecraft" : "Account Twitch collegato"}</span>
                <span>Username: {twitchData?.login}</span>
                {twitchData?.banned ? (
                    <span className={styles.notConnected}>🚫 Bannato</span>
                ) : (
                    <div className={styles.infoLine}>
                        <span>
                            Stato Sub: {
                            twitchData?.subActive ? (
                                <>
                                    ✅ Attivo - Tier {
                                    twitchData?.subTier === "1000" ? '1' :
                                        twitchData?.subTier === "2000" ? '2' :
                                            twitchData?.subTier === "3000" ? '3' :
                                                'Sconosciuto'
                                }
                                </>
                            ) : (
                                <>❌ Non attivo</>
                            )
                        }
                        </span>
                        <span>
                            Stato Vip: {
                            twitchData?.vip ? (
                                <>✅ Attivo</>
                            ) : (
                                <>❌ Non attivo</>
                            )
                        }
                        </span>
                    </div>
                )}
                {minecraftConnected ? null : <CustomButton onClick={loadTwitchConnection}>Cambia account</CustomButton>}
            </>
        )
    }

    const Profile = () => {
        return (
            <div className={styles.profileBox}>
                <div className={styles.sectionTitle}>Profilo</div>
                <div className={styles.twoColumns}>
                    <div className={styles.skin}>
                        {minecraftData?.uuid ? (
                            <>
                                <LazyLoadImage
                                    src={"https://mc-heads.net/body/" + minecraftData.uuid + "/right/.png"}/>
                            </>
                        ) : (
                            <>
                                <LazyLoadImage style={{filter: "grayscale(1)"}}
                                               src={"https://mc-heads.net/body/steve/right/.png"}/>
                            </>
                        )}
                    </div>
                    <div className={styles.info}>
                        <div className={styles.infoLine}>
                            <span>Nome utente: {accountData?.username} <CloudImage
                                uuid={accountData?.staffGroup.iconUUID}/></span>

                            <CustomButton onClick={() => {
                                ApiInterface.logout();
                            }}>Fai il Logout</CustomButton>
                            <CustomButton onClick={() => {
                                ApiInterface.logoutAll();
                            }}>Disconnetti tutti i dispositivi</CustomButton>
                        </div><br/>
                        <div className={styles.infoLine}>
                            <span>📩Report totali: {reportsData?.total}</span>
                            <span>📬Report aperti: {reportsData?.open}</span>
                            <span>📪Report chiusi: {reportsData?.closed}</span>
                            {(reportsData?.total ?? 0) - ((reportsData?.open ?? 0) + (reportsData?.closed ?? 0)) > 0 && (
                                <span>🫥 Report nascosti: {
                                    (reportsData?.total ?? 0) - ((reportsData?.open ?? 0) + (reportsData?.closed ?? 0))
                                }</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const Connections = () => {
        return (
            <>
                <div className={styles.connectionsBox}>
                    <div className={styles.sectionTitle}>Collegamento Minecraft</div>
                    {getMinecraftBox()}
                </div>
                <div className={styles.connectionsBox}>
                    <div className={styles.sectionTitle}>Collegamento Twitch</div>
                    {getTwitchBox()}
                </div>
            </>
        )
    }

    const Settings = () => {
        return (
            <div className={styles.sectionBox}>
                <div className={styles.sectionTitle}>Impostazioni Account</div>
                {settingType === 'none' && (
                    <>
                        <div className={styles.infoLine}>
                            {accountData?.emailVerified ? '' : (
                                <>
                                    <span className={styles.notConnected}>Email non verificata</span>
                                    <CustomButton disabled={sendingEmailButtonDisabled} onClick={() => sendVerifyEmail()}>Manda nuova mail di Verifica</CustomButton>
                                </>
                            )}
                            <span>Email attuale:<br/>{accountData?.email} </span>
                            <CustomButton onClick={() => setSettingType('email')}>Cambia
                                email</CustomButton>
                        </div>
                        <div className={styles.infoLine}>
                            <span>Password</span>
                            <CustomButton onClick={() => setSettingType('password')}>Cambia
                                password</CustomButton>
                        </div>
                    </>
                )}
                {settingType === 'email' && (
                    <>
                        <FloatingInput type="email" label="Nuova Email" id="email" autoComplete={"new-email"}/>
                        <FloatingInput type="password" label="Password attuale" id="password"/>
                        <div style={{display: 'flex', gap: '10px'}}>
                            <CustomButton onClick={changeAccountData}>Conferma</CustomButton>
                            <CustomButton
                                onClick={() => setSettingType('none')}>Annulla</CustomButton>
                        </div>
                    </>
                )}
                {settingType === 'password' && (
                    <>
                        <FloatingInput type="password" label="Nuova Password" id="newPassword"
                                       autoComplete={"new-password"}/>
                        <FloatingInput type="password" label="Conferma Password" autoComplete={"new-password"}
                                       id="newPasswordConfirm"/>
                        <FloatingInput type="password" label="Password attuale" id="password"/>
                        <div style={{display: 'flex', gap: '10px'}}>
                            <CustomButton onClick={changeAccountData}>Conferma</CustomButton>
                            <CustomButton
                                onClick={() => setSettingType('none')}>Annulla</CustomButton>
                        </div>
                    </>
                )}
            </div>
        )
    }

    return (
        <>
            <div className="page__body">
                <Header/>
                <div className="page__content">
                    <div className={styles.profileWrapper}>
                        <div className={styles.profileContainerBox}>
                            <div className={styles.tabNav}>
                                <CustomButton className={page === 'profile' ? styles.activePage : ''}
                                        onClick={() => setPage('profile')}>Profilo
                                </CustomButton>
                                <CustomButton className={page === 'connections' ? styles.activePage : ''}
                                        onClick={() => setPage('connections')}>Collegamenti
                                </CustomButton>
                                <CustomButton className={page === 'settings' ? styles.activePage : ''}
                                        onClick={() => setPage('settings')}>Impostazioni
                                </CustomButton>
                            </div>
                            <div className={styles.content}>
                                {page === 'profile' && <Profile/>}
                                {page === 'connections' && <Connections/>}
                                {page === 'settings' && <Settings/>}
                            </div>
                        </div>
                    </div>
                </div>
                <Footer/>
            </div>
        </>
    );
};

export default Profile;
